<script setup lang="ts">
import type { NuxtPicture } from "#components";
import { withBase } from "ufo";

const emit = defineEmits<{
  "click:image": [item: GalleryItem];
  load: [];
}>();

const props = defineProps<{
  item: GalleryItem;
  index?: number;

  isOpen?: boolean;

  paddedActions?: boolean;
  hideDetails?: boolean;
}>();

const pic = ref<typeof NuxtPicture>();
const { width } = useElementSize(() => pic.value?.$el);
const { isMobile } = useUserDevice();
const { isIos, isSafari } = useDevice();

const { currentPalette } = useTheme();

const clickCount = refAutoReset(0, 300);

const handleClick = () => {
  clickCount.value += 1;
};

watch(clickCount, (curr, prev) => {
  if (prev === 1 && curr === 0) {
    emit("click:image", props.item);
  }
});

const config = useRuntimeConfig();

const getAsstetUrl = (item: FileItem) =>
  withBase(
    `/assets/${item.id}` +
      (item.filename_download
        ? `/${encodeURIComponent(item.filename_download)}`
        : ""),
    config.public.directus.url
  );

const key = ref(0);

onMounted(() => {
  key.value += 1;
});

const loaded = ref(false);

whenever(
  () => loaded.value,
  () => {
    emit("load");
    key.value += 1;
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex flex-col items-center justify-center w-full h-full overflow-hidden relative"
    @click="handleClick"
  >
    <figure
      class="flex flex-col items-center max-w-full max-h-full min-h-0 min-w-0 swiper-zoom-container !static"
      :class="{ '!w-fit !h-fit': !isIos && !isSafari }"
    >
      <NuxtPicture
        :key="key"
        v-if="item.collection === 'images'"
        ref="pic"
        provider="directus"
        :src="
          item.item.file?.id +
          (item.item.file?.filename_download
            ? '/' + encodeURIComponent(item.item.file.filename_download)
            : '')
        "
        :width="item.item.file?.width"
        :height="item.item.file?.height"
        :alt="item.item.file?.description || item.item.caption"
        sizes="100vw sm:100vw md:100vw lg:100vw xl:100vw"
        class="grow flex flex-col items-center justify-center w-full h-full max-w-max max-h-max min-w-0 min-h-0 z-10"
        :img-attrs="{
          class: [
            'block w-auto h-auto max-w-full max-h-full min-w-0 min-h-0 object-contain',
            currentPalette.bg,
          ],
        }"
        quality="100"
        preload
        @load="loaded = true"
      />

      <VideoPlayer
        v-else-if="item.collection === 'videos'"
        ref="pic"
        :width="item.item.file?.width"
        :height="item.item.file?.height"
        class="grow flex flex-col items-center justify-center w-full h-full max-w-max max-h-max min-w-0 min-h-0 z-10"
        :poster="item.item.poster ? getAsstetUrl(item.item.poster) : undefined"
        :class="[currentPalette.bg]"
        custom-controls
      >
        <source
          v-if="item.item.file"
          :src="getAsstetUrl(item.item.file)"
          :type="item.item.file.type"
        />
        Twoja przeglądarka nie obsługuje odtwarzacza wideo.
      </VideoPlayer>

      <figcaption
        v-if="!isMobile"
        v-show="!hideDetails"
        :class="[
          'max-w-full min-w-0',
          'flex items-center gap-4',
          'text-grey lg:text-grey-50',
          'text-center lg:text-start',
          'text-[0.5rem] leading-4',
          'px-4 lg:px-0',
          { 'mt-4': paddedActions && !isMobile },
        ]"
        class="will-change-[width]"
        :style="{ width: isMobile ? '100%' : `${width}px` }"
      >
        <div
          class="grow whitespace-nowrap min-w-0 overflow-hidden text-ellipsis"
        >
          {{ item.item.caption }}
        </div>

        <div v-if="$slots['actions']" class="flex items-center justify-end">
          <slot name="actions" v-bind="{ props, item }"></slot>
        </div>
      </figcaption>
    </figure>

    <div
      v-if="isMobile"
      v-show="!hideDetails"
      :class="[
        'absolute bottom-0 inset-x-0 z-10',
        'max-w-full min-w-0',
        'w-full',
        'flex flex-col items-stretch gap-4',
        'text-white',
        'text-[0.5rem] leading-4',
        'p-4',
        'bg-gradient-to-t from-black to-transparent',
        'min-h-12',
        { 'mt-4': paddedActions && !isMobile },
      ]"
      class="will-change-[width]"
      :style="{ width: isMobile ? '100%' : `${width}px` }"
    >
      <p class="grow whitespace-nowrap min-w-0 overflow-hidden text-ellipsis">
        {{ item.item.caption }}
      </p>

      <div
        v-if="$slots['actions']"
        class="flex flex-col items-stretch justify-end"
      >
        <slot name="actions" v-bind="{ props, item }"></slot>
      </div>
    </div>
  </div>
</template>
